import React, { useEffect, useState } from 'react';
import { Phone, MapPin, Menu, X, ChevronDown, Facebook } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { PHONE, PHONE_TEL, GOOGLE_MAPS_URL, FACEBOOK_URL } from '../constants/business';
type DropItem = {
  name: string;
  href: string;
  external?: boolean;
};
type NavItem = {
  name: string;
  href: string;
  dropdown?: DropItem[];
};
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  const navItems: NavItem[] = [{
    name: 'Home',
    href: '/'
  }, {
    name: 'About',
    href: '/about'
  }, {
    name: 'In-Store',
    href: '/store-tour',
    dropdown: [{
      name: 'Store Tour',
      href: '/store-tour'
    }, {
      name: 'Firearms',
      href: '/firearms'
    }, {
      name: 'Ammunition',
      href: '/ammunition'
    }, {
      name: 'Optics',
      href: '/optics'
    }, {
      name: 'Reloading Supplies',
      href: '/reloading-supplies'
    }, {
      name: 'Safes & Accessories',
      href: '/safes-accessories'
    }, {
      name: 'Hunting Gear & Outdoor Gear',
      href: '/hunting-gear-blinds'
    }]
  }, {
    name: 'Services & Classes',
    href: '/services',
    dropdown: [{
      name: 'Services & In-Store Support',
      href: '/services'
    }, {
      name: 'Minnesota Permit to Carry Classes',
      href: '/mn-permit-to-carry-classes'
    }, {
      name: 'Transfers & Regulated Services',
      href: '/services'
    }, {
      name: 'Events & Demo Shoots',
      href: '/events'
    }]
  }, {
    name: 'Events',
    href: '/events'
  }, {
    name: 'Contact',
    href: '/contact',
    dropdown: [{
      name: 'Contact',
      href: '/contact'
    }, {
      name: 'FAQ',
      href: '/faq'
    }, {
      name: 'Facebook',
      href: FACEBOOK_URL,
      external: true
    }]
  }];
  const renderDropItem = (dropItem: DropItem) => {
    if (dropItem.external) {
      return <a key={dropItem.name} href={dropItem.href} target="_blank" rel="noopener noreferrer" className="block px-4 py-2.5 text-sm text-brand-charcoal hover:bg-brand-bone hover:text-brand-hunter transition-colors" data-id="element-67">
          {dropItem.name}
        </a>;
    }
    return <Link key={dropItem.name} to={dropItem.href} className="block px-4 py-2.5 text-sm text-brand-charcoal hover:bg-brand-bone hover:text-brand-hunter transition-colors" data-id="element-68">
        {dropItem.name}
      </Link>;
  };
  return <header className={`sticky top-0 z-50 bg-brand-offwhite transition-shadow duration-300 ${isScrolled ? 'shadow-md' : 'shadow-sm'}`} data-id="element-69">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-70">
        <div className="flex justify-between items-center py-3 gap-6" data-id="element-71">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-id="element-72">
            <img src="https://drwzpk38qkpfb.cloudfront.net/www.ksportsllc.com/uploaded/images/Updated-logo.png?opt=w3" alt="Kjergaard Sports logo" className="h-14 md:h-20 w-auto" data-id="element-73" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8 flex-1 justify-center" data-id="element-74">
            {navItems.map(item => <div key={item.name} className="relative" onMouseEnter={() => setActiveDropdown(item.name)} onMouseLeave={() => setActiveDropdown(null)} data-id="element-75">
                <Link to={item.href} className={`text-brand-charcoal hover:text-brand-hunter font-medium text-base flex items-center transition-colors py-2 whitespace-nowrap ${location.pathname === item.href ? 'text-brand-hunter' : ''}`} data-id="element-76">
                  {item.name}
                  {item.dropdown && <ChevronDown className="ml-1 w-4 h-4 text-brand-charcoal/60" data-id="element-77" />}
                </Link>

                {/* Dropdown */}
                {item.dropdown && <AnimatePresence data-id="element-78">
                    {activeDropdown === item.name && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 8
              }} transition={{
                duration: 0.18
              }} className="absolute top-full left-0 mt-1 w-64 bg-brand-cream border border-brand-bone shadow-lg rounded-sm overflow-hidden" data-id="element-79">
                        <div className="py-2" data-id="element-80">
                          {item.dropdown.map(renderDropItem)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>}
              </div>)}
          </nav>

          {/* Desktop CTAs — phone primary, directions secondary */}
          <div className="hidden lg:flex items-center space-x-3 flex-shrink-0" data-id="element-81">
            <a href={PHONE_TEL} className="flex items-center text-brand-hunter hover:text-brand-wood font-semibold text-base transition-colors whitespace-nowrap" data-id="element-82">
              <Phone className="w-5 h-5 mr-2" data-id="element-83" />
              {PHONE}
            </a>
            <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" aria-label="Get directions" title="Get Directions" className="w-10 h-10 rounded-sm bg-brand-hunter hover:bg-brand-wood text-brand-offwhite flex items-center justify-center transition-colors" data-id="element-84">
              <MapPin className="w-5 h-5" data-id="element-85" />
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex lg:hidden items-center space-x-3" data-id="element-86">
            <a href={PHONE_TEL} className="text-brand-hunter p-2" aria-label="Call store" data-id="element-87">
              <Phone className="w-6 h-6" data-id="element-88" />
            </a>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-brand-charcoal p-2" aria-label="Toggle menu" data-id="element-89">
              {mobileMenuOpen ? <X className="w-7 h-7" data-id="element-90" /> : <Menu className="w-7 h-7" data-id="element-91" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-92">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: '100vh'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden absolute top-full left-0 right-0 bg-brand-offwhite border-t border-brand-bone overflow-y-auto pb-32" data-id="element-93">
            <div className="px-4 py-6 space-y-6" data-id="element-94">
              {navItems.map(item => <div key={item.name} className="border-b border-brand-bone pb-4" data-id="element-95">
                  <Link to={item.href} className="text-lg font-serif text-brand-charcoal font-medium block" data-id="element-96">
                    {item.name}
                  </Link>
                  {item.dropdown && <div className="mt-3 pl-4 space-y-3 border-l-2 border-brand-bone" data-id="element-97">
                      {item.dropdown.map(dropItem => dropItem.external ? <a key={dropItem.name} href={dropItem.href} target="_blank" rel="noopener noreferrer" className="block text-brand-charcoal/80" data-id="element-98">
                            {dropItem.name}
                          </a> : <Link key={dropItem.name} to={dropItem.href} className="block text-brand-charcoal/80" data-id="element-99">
                            {dropItem.name}
                          </Link>)}
                    </div>}
                </div>)}

              <div className="pt-4 space-y-3" data-id="element-100">
                <a href={PHONE_TEL} className="w-full bg-brand-hunter text-brand-offwhite px-5 py-3 rounded-sm font-medium flex items-center justify-center" data-id="element-101">
                  <Phone className="w-5 h-5 mr-2" data-id="element-102" />
                  Call {PHONE}
                </a>
                <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="w-full border border-brand-hunter text-brand-hunter px-5 py-3 rounded-sm font-medium flex items-center justify-center" data-id="element-103">
                  <MapPin className="w-5 h-5 mr-2" data-id="element-104" />
                  Get Directions
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}